import { Col, DatePicker, Radio, Row, Select } from "antd";
import useUsers from "hooks/useUsers";
import { DisplayDateFormat } from "models/websiteSettings";
import { Moment } from "moment-timezone";
import { FC, useCallback, useState } from "react";
import { DefaultPageRequest } from "util/getDefaultPageRequest";

const { RangePicker } = DatePicker;

// metrics were not tracked before this date
const before2025Oct31 = (date: Moment) => {
    return date.isBefore('2025-10-31');
}

export enum MetricsType {
    Unknown = 0,
    Puller = 1,
    Shipper = 2
}

export enum Timeframe {
    Unknown = 0,
    Last24Hours = 1,
    Last7Days = 2,
    Last30Days = 3,
    Last120Days = 4,
    Custom = 5
}

export interface ToolbarOptions {
    type: MetricsType;
    userId?: string;
    timeframe: Timeframe;
    customStart: Moment | null;
    customEnd: Moment | null;
}

export const DefaultOptions = {
    type: MetricsType.Puller,
    timeframe: Timeframe.Last24Hours,
    customStart: null,
    customEnd: null
};

const MetricsToolbar: FC<{
    onChange: (options: ToolbarOptions) => void
}> = ({ onChange }) => {
    const [options, setOptions] = useState<ToolbarOptions>(DefaultOptions);

    const { data } = useUsers({
        ...DefaultPageRequest,
        pageSize: 100,
        filterBy: { "Roles": ['staff'] },
        sort: ["UserName", true]
    });
    const users = data?.data ?? [];

    const onOptionChange = useCallback((
        field: keyof ToolbarOptions,
        value: any,
        suppressEvent: boolean = false) => {
        setOptions((prev) => {
            const newOpts = { ...prev, [field]: value };
            if (!suppressEvent) {
                onChange(newOpts);
            }
            return newOpts;
        });
    }, [onChange]);

    return <Row gutter={[32, 32]} style={{ marginBottom: '20px' }}>
        <Col span={4}>
            <Radio.Group
                size="large"
                optionType="button"
                buttonStyle="solid"
                value={options.type}
                options={[
                    { label: "Pulling", value: MetricsType.Puller },
                    { label: "Shipping", value: MetricsType.Shipper }
                ]}
                onChange={(e) => onOptionChange("type", e.target.value)}
            />
        </Col>
        <Col span={4}>
            <Select
                size="large"
                placeholder="All Users"
                value={options.userId}
                options={users.map(u => ({
                    label: u.userName,
                    value: u.id
                }))}
                style={{ width: '100%' }}
                allowClear
                onChange={(v) => onOptionChange("userId", v)}
            />
        </Col>
        <Col span={3}>
            <Select
                size="large"
                placeholder="Timeframe"
                style={{ width: '100%' }}
                value={options.timeframe}
                options={[
                    { label: "Last 24 Hours", value: Timeframe.Last24Hours },
                    { label: "Last 7 Days", value: Timeframe.Last7Days },
                    { label: "Last 30 Days", value: Timeframe.Last30Days },
                    { label: "Last 120 Days", value: Timeframe.Last120Days },
                    { label: "Custom...", value: Timeframe.Custom },
                ]}
                onChange={(v) => {
                    onOptionChange("customStart", null, true);
                    onOptionChange("customEnd", null, true);
                    onOptionChange("timeframe", v, v === Timeframe.Custom);
                }}
            />
        </Col>
        <Col span={5}>
            {options.timeframe === Timeframe.Custom && <RangePicker
                size="large"
                format={DisplayDateFormat}
                disabledDate={before2025Oct31}
                onChange={(range) => {
                    const [start, end] = range ?? [];
                    onOptionChange("customStart", start, true);
                    onOptionChange("customEnd", end, (!start && !end));
                }}
                value={[options.customStart, options.customEnd]}
            />}
        </Col>
    </Row>
};

export default MetricsToolbar;